import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
} from "@/components/ui/item";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { type WorkflowGroup, type WorkflowMetric } from "./types";

function MetricRow({ label, values, icon: Icon, subMetrics }: WorkflowMetric) {
  // Values always sit in the right-hand column; several stack, so the row aligns to
  // the top instead of centring the label against the whole stack.
  const isStacked = values.length > 1;
  const hasSubMetrics = Boolean(subMetrics?.length);

  return (
    <>
      <Item
        className={cn(
          "gap-3 border-none",
          isStacked ? "items-start" : "items-center",
          hasSubMetrics && "pb-1"
        )}
      >
        <ItemMedia>
          <Icon className="size-5 text-neutral-500" aria-hidden="true" />
        </ItemMedia>
        <ItemContent className="min-w-0 flex-1 gap-0">
          {/* leading-6 matches the values, so the label shares the first one's line. */}
          <ItemDescription className="text-neutral-600 leading-6">{label}</ItemDescription>
        </ItemContent>
        <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
          {values.map(({ display, tooltip }, index) => (
            // Index key: the list is static for a given render and order is meaningful.
            <ItemTitle key={index} className="text-base font-semibold leading-6">
              {tooltip ? (
                <Tooltip>
                  {/* tabIndex: the exact figure has to be reachable without a pointer. */}
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="cursor-help underline decoration-dotted underline-offset-4"
                    >
                      {display}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={6}>
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              ) : (
                display
              )}
            </ItemTitle>
          ))}
        </div>
      </Item>
      {subMetrics?.length ? (
        // pl-12 lines the breakdown up with the parent label: Item p-4 + size-5 icon + gap-3.
        <div className="flex flex-col gap-1 pr-4 pb-3 pl-12">
          {subMetrics.map(({ id, label: subLabel, value }) => (
            <div key={id} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 text-neutral-500 text-sm">{subLabel}</span>
              <span className="shrink-0 font-medium text-neutral-700 text-sm">{value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

interface WorkflowCardProps extends WorkflowGroup {
  /**
   * `horizontal` lays the metrics out as columns instead of stacked rows, for the
   * full-width card that spans the grid.
   */
  orientation?: "vertical" | "horizontal";
}

export function WorkflowCard({
  title,
  icon: Icon,
  metrics,
  orientation = "vertical",
}: WorkflowCardProps) {
  return (
    <Card className="h-full gap-0 border-none p-0">
      <CardHeader className="gap-0 rounded-t-xl bg-blue-50 px-4 py-3">
        <CardTitle className="flex items-center justify-center gap-2 text-base font-semibold text-blue-700">
          <Icon className="size-5 shrink-0" aria-hidden="true" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col px-0 pb-2">
        {orientation === "horizontal" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5">
            {metrics.map((metric, index) => (
              <div
                key={metric.id}
                // Stacked below lg, so the divider turns from a rule above into one alongside.
                // border-border matches ItemSeparator; Tailwind v4 defaults borders to currentColor.
                className={
                  index > 0 ? "border-border border-t lg:border-t-0 lg:border-l" : undefined
                }
              >
                <MetricRow {...metric} />
              </div>
            ))}
          </div>
        ) : (
          <ItemGroup>
            {metrics.map((metric, index) => (
              <div key={metric.id}>
                {index > 0 ? <ItemSeparator /> : null}
                <MetricRow {...metric} />
              </div>
            ))}
          </ItemGroup>
        )}
      </CardContent>
    </Card>
  );
}
